import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { merge, Observable, of, timer } from 'rxjs';
import { catchError, filter, map, repeat, retry, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import * as CvParseActions from '../actions/cv-parse.actions';
import * as NotificationsActions from '../actions/notifications.actions';
import { CvParseService } from '../../services/cv/cv-parse.service';
import { HttpErrorResponse } from '@angular/common/http';
import { IActiveParsingResponse, PARSE_STATE } from '../../interfaces/cv-parse';
import { selectParsingState } from '../reducers/cv/cv-parse.selectors';
import { SnackBarService, SnackBarTypes } from '@my7n/ui';

const PARSING_RESULT_DELAY = 5000;

const STOP_REPEATING_PARSE_CODES = [404, 410, 424, 500];

@Injectable()
export class CvParseEffects {
  constructor(
    private actions$: Actions,
    private cvParseService: CvParseService,
    private store: Store,
    private snackbarService: SnackBarService
  ) {}

  checkActiveParsing$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CvParseActions.checkActiveParsing),
      switchMap(() =>
        this.cvParseService.getUserActiveParsing().pipe(
          map((response: IActiveParsingResponse) => {
            if (response && response.RequestId) {
              return CvParseActions.checkActiveParsingSuccess({ activeParsing: response });
            } else {
              return CvParseActions.noActiveParsing();
            }
          }),
          catchError(() => of(CvParseActions.checkActiveParsingError()))
        )
      )
    )
  );

  uploadFileToParse$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(CvParseActions.uploadFileToParse),
      switchMap(({ userId, cvFile }) => {
        this.snackbarService.openPending({
          message: 'Uploading CV for parsing...',
          type: SnackBarTypes.Notification,
        });

        if (!userId) {
          return this.cvParseService.uploadFileToParse(cvFile).pipe(
            map((response: IActiveParsingResponse) => {
              this.snackbarService.close();
              return CvParseActions.uploadFileToParseSuccess({ activeParsing: response });
            }),
            catchError(() => {
              this.snackbarService.open({
                message: 'Failed to upload CV for parsing.',
                type: SnackBarTypes.ErrorAlt,
              });

              return of(CvParseActions.uploadFileToParseError());
            })
          );
        } else {
          return this.cvParseService.uploadFileToParseByAgent(userId, cvFile).pipe(
            map((response: IActiveParsingResponse) => {
              this.snackbarService.close();
              return CvParseActions.uploadFileToParseSuccess({ activeParsing: response });
            }),
            catchError(() => {
              this.snackbarService.open({
                message: 'Failed to upload CV for parsing.',
                type: SnackBarTypes.ErrorAlt,
              });

              return of(CvParseActions.uploadFileToParseError());
            })
          );
        }
      })
    )
  );

  startParsing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvParseActions.uploadFileToParseSuccess, CvParseActions.checkActiveParsingSuccess),
      switchMap((action) => of(CvParseActions.getParsingResultForId({ cvId: action.activeParsing.CvId })))
    )
  );

  getParsingResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvParseActions.getParsingResultForId),
      switchMap(({ cvId }) => {
        const cancel$ = this.store.select(selectParsingState).pipe(filter((state) => state === PARSE_STATE.CANCELLED));
        const parsedSuccessfully$ = this.store.select(selectParsingState).pipe(filter((state) => state === PARSE_STATE.PARSED_SUCCESS));
        const stopRepeating$ = merge(cancel$, parsedSuccessfully$);

        return this.cvParseService.getParsingResultByCvId(cvId).pipe(
          // It repeats call on success case with delay
          repeat({ delay: PARSING_RESULT_DELAY }),
          // It repeats on error case with delay but only when error is not in stop list
          retry({
            delay: (err: HttpErrorResponse) => {
              // Stop retrying immediately
              if (STOP_REPEATING_PARSE_CODES.includes(err.status)) {
                throw err;
              }

              // Retry after delay
              return timer(PARSING_RESULT_DELAY);
            },
          }),
          // Stop repeating when parsing is cancelled or succeeded - this will stop repeat and retry as well
          takeUntil(stopRepeating$),
          // Stop repeating when we get final result - works only for success cases
          takeWhile(
            (response) => response.status !== 200,
            true // include the final emission
          ),
          map((response) =>
            response.status === 200
              ? CvParseActions.getParsingResultForIdSuccess({ cvId })
              : CvParseActions.getParsingResultForIdNoResultYet({ cvId })
          ),
          catchError((err: HttpErrorResponse) => {
            // Continue repeating on 503 when pod is restarting
            if (err.status === 503) {
              return of(CvParseActions.getParsingResultForIdNoResultYet({ cvId }));
            }
            
            // Return cancel action when successfully cancelled by someone (will stop repeating)
            if (err.status === 410) {
              return of(CvParseActions.cancelActiveParsingSuccess());
            }

            // Handle other errors
            return of(CvParseActions.getParsingResultForIdError());
          })
        );
      })
    )
  );

  cancelActiveParsing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CvParseActions.cancelActiveParsing),
      switchMap(({ cvId }) =>
        this.cvParseService.cancelParsing(cvId).pipe(
          map(() => CvParseActions.cancelActiveParsingSuccess()),
          catchError(() => of(CvParseActions.cancelActiveParsingError()))
        )
      )
    )
  );

  reloadNotificationsAfterParsing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CvParseActions.getParsingResultForIdError,
        CvParseActions.getParsingResultForIdSuccess
      ),
      map(() => NotificationsActions.queryNotifications())
    )
  );
}
